'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Counter — animates from 0 up to `to` the first time it scrolls into view.
 * SSR renders the final value, so no-JS and reduced-motion users always see
 * the real number; the count-up only runs for motion-OK users on intersect.
 */
export function Counter({
  to,
  className,
  suffix = '',
}: {
  to: number;
  className?: string;
  suffix?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(to);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = ref.current;

    if (!el) return;

    let raf = 0;
    let start: number | null = null;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        io.disconnect();
        setValue(0);

        const duration = 1200;
        const step = (t: number) => {
          if (start === null) start = t;

          const progress = Math.min(1, (t - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);

          setValue(Math.round(eased * to));

          if (progress < 1) raf = requestAnimationFrame(step);
        };

        raf = requestAnimationFrame(step);
      },
      { threshold: 0.5 },
    );

    io.observe(el);

    return () => {
      io.disconnect();

      if (raf) cancelAnimationFrame(raf);
    };
  }, [to]);

  return (
    <span ref={ref} className={className}>
      {/* Reserve the final width so the count-up never reflows its neighbours. */}
      <span
        className="inline-block text-right tabular-nums"
        style={{ minWidth: `${String(to).length}ch` }}
      >
        {value}
      </span>
      {suffix}
    </span>
  );
}
